import { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import type { HotelOverviewData } from '../types';
import { trackExpandRoomDetails } from '../utils/tracking';

interface HotelOverviewProps {
  hotel: HotelOverviewData['hotel'];
  rooms: HotelOverviewData['rooms'];
  onRoomSelect?: (packageReference: string, roomName: string) => void;
  translations?: {
    viewAvailability?: string;
    viewDetails?: string;
    roomDescription?: string;
    roomFeatures?: string;
    from?: string;
    perNight?: string;
    guests?: string;
    newBadge?: string;
  };
}

// Simple Button component
const Button: React.FC<{
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  variant?: 'default' | 'outline' | 'icon';
  className?: string;
}> = ({ onClick, children, variant = 'default', className = '' }) => {
  const baseClasses = 'inline-flex items-center justify-center rounded transition-colors duration-200';
  const variantClasses = {
    default: 'bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4',
    outline: 'border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4',
    icon: 'bg-white/80 hover:bg-white p-2 text-gray-800 shadow-md'
  };

  return (
    <button onClick={onClick} className={`${baseClasses} ${variantClasses[variant]} ${className}`}>
      {children}
    </button>
  );
};

// Image Gallery Component with swipe gestures
const ImageGallery: React.FC<{
  images: { src: string; alt: string }[];
}> = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const isLastSlide = currentIndex === images.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };

  const goToSlide = (slideIndex: number) => {
    setCurrentIndex(slideIndex);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // Swiped left
      goToNext();
    }

    if (touchStart - touchEnd < -75) {
      // Swiped right
      goToPrevious();
    }
  };

  if (images.length === 0) {
    return (
      <div className="relative h-48 bg-gray-200 flex items-center justify-center">
        <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      </div>
    );
  }

  return (
    <div
      className="relative h-48 overflow-hidden select-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <img
        src={images[currentIndex].src}
        alt={images[currentIndex].alt}
        className="w-full h-full object-cover"
        draggable={false}
      />

      {images.length > 1 && (
        <>
          {/* Previous Button */}
          <div className="absolute top-1/2 -translate-y-1/2 left-2">
            <Button variant="icon" onClick={(e) => { e.stopPropagation(); goToPrevious(); }}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
          </div>

          {/* Next Button */}
          <div className="absolute top-1/2 -translate-y-1/2 right-2">
            <Button variant="icon" onClick={(e) => { e.stopPropagation(); goToNext(); }}>
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Dot Indicators */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-2">
            {images.map((_, slideIndex) => (
              <div
                key={slideIndex}
                className={`h-2 w-2 rounded-full cursor-pointer transition-colors ${
                  slideIndex === currentIndex ? 'bg-white' : 'bg-white/50'
                }`}
                onClick={(e) => { e.stopPropagation(); goToSlide(slideIndex); }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Collapsible Room Details Component
const RoomDetailsCollapsible: React.FC<{
  room: HotelOverviewData['rooms'][0];
  hotelId: string;
  translations: HotelOverviewProps['translations'];
}> = ({ room, hotelId, translations }) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasDetails = room.description || (room.features && room.features.length > 0);

  if (!hasDetails) {
    return null;
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isOpen) {
      trackExpandRoomDetails(hotelId, room.packageReference, room.name);
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="mt-2">
      <Button
        variant="outline"
        className="w-full flex justify-between items-center"
        onClick={handleToggle}
      >
        <span>{translations?.viewDetails || 'View Details'}</span>
        {isOpen ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
      </Button>

      {isOpen && (
        <div className="mt-4 space-y-4 text-left" onClick={(e) => e.stopPropagation()}>
          {room.description && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">
                {translations?.roomDescription || 'Room Description'}
              </h4>
              <p className="text-sm text-gray-600">{room.description}</p>
            </div>
          )}

          {room.features && room.features.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">
                {translations?.roomFeatures || 'Room Features'}
              </h4>
              <ul className="space-y-2">
                {room.features.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2 text-gray-700">
                    <span className="text-blue-600">{feature.icon}</span>
                    <span className="text-sm">{feature.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export function HotelOverview({ hotel, rooms, onRoomSelect, translations }: HotelOverviewProps) {

  const handleRoomClick = (room: typeof rooms[0]) => {
    // Get current page URL path
    const currentPath = window.location.pathname;

    // Ensure path ends with slash for clean concatenation
    const basePath = currentPath.endsWith('/') ? currentPath : `${currentPath}/`;

    // Build room URL by extending current path with packageReference
    const roomUrl = `${basePath}${room.packageReference}`.replace(/\/+/g, '/');

    // Navigate to room page
    window.location.href = roomUrl;

    // Call callback if provided (for analytics, tracking, etc.)
    if (onRoomSelect) {
      onRoomSelect(room.packageReference, room.name);
    }
  };

  return (
    <div className="momentje-hotel-overview">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{hotel.name}</h1>
        {hotel.description && (
          <p className="text-gray-600">{hotel.description}</p>
        )}
      </div>

      {/* Room Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rooms.map((room) => (
          <div
            key={room.id}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer flex flex-col relative"
            onClick={() => handleRoomClick(room)}
          >
            {/* New Badge */}
            {room.isNew && (
              <div className="absolute top-3 right-3 z-10">
                <span className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg border border-red-400/20">
                  {(translations?.newBadge || 'NEW').toUpperCase()}
                </span>
              </div>
            )}

            {/* Room Image Gallery */}
            <ImageGallery images={room.images || []} />

            {/* Room Info */}
            <div className="p-4 flex flex-col flex-grow">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{room.name}</h3>

              {/* Room Details */}
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                {room.capacity && (
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>{room.capacity} {translations?.guests || 'guests'}</span>
                  </div>
                )}

                {room.beds && (
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <span>{room.beds}</span>
                  </div>
                )}
              </div>

              {/* Price (if available) */}
              {room.price && (
                <div className="mb-4">
                  <span className="text-sm text-gray-500">{translations?.from || 'From'} </span>
                  <span className="text-lg font-bold text-blue-600">{room.price}</span>
                  <span className="text-sm text-gray-500"> {translations?.perNight || '/ night'}</span>
                </div>
              )}

              {/* CTA Button */}
              <div className="mt-auto">
                <Button
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRoomClick(room);
                  }}
                >
                  {translations?.viewAvailability || 'View Availability'}
                </Button>
              </div>

              {/* Collapsible Details */}
              <RoomDetailsCollapsible room={room} hotelId={hotel.slug} translations={translations} />
            </div>
          </div>
        ))}
      </div>

      {/* No Rooms Message */}
      {rooms.length === 0 && (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <p className="text-gray-600">No rooms available at this time.</p>
        </div>
      )}
    </div>
  );
}
